// Offline-tolerant queue for scorecard writes.
//
// Motivating scenario (real, from field testing):
//   • Group tees off at a course with clubhouse Wi-Fi.
//   • Cellular drops mid-round somewhere on the back nine.
//   • Player keeps scoring — writes should queue locally, not fail
//     silently and lose data.
//   • On return to any network (or on return to visible), the queue
//     drains in order.
//
// Design:
//   • Every entry keyed by (round_id, player_id, hole_number) so a
//     later save on the same hole *replaces* the earlier one instead of
//     stacking. Prevents queue bloat when a scorer taps ±/± repeatedly.
//   • Payload is the same shape upsert_standalone_score expects, plus a
//     client-generated `client_updated_at` so the server side (or a
//     future last-writer-wins merge) can order writes correctly even
//     across offline gaps.
//   • Drain triggers: `online`, `visibilitychange → visible`, and a
//     manual `flushPending()` call after each save. Every attempt is
//     idempotent (upsert on the server side); replays are safe.
//   • Silently no-op when localStorage is unavailable (private browsing
//     modes). The scorecard's in-memory state still holds the score
//     during the session — degraded but not broken.

const QUEUE_KEY = 'tf:pendingWrites:v1'
const DEAD_LETTER_KEY = 'tf:pendingWrites:dead:v1'
const MAX_QUEUE = 500        // guard against runaway growth if drain is stuck
const MAX_ATTEMPTS = 5       // per-entry retry ceiling; over → dead-letter
const MAX_DEAD_LETTER = 100  // keep the last N dropped entries for forensics

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch { return [] }
}

function writeQueue(entries) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(entries.slice(-MAX_QUEUE))) }
  catch { /* quota — nothing we can do */ }
}

// Key that dedups a write. Same action + round + player = same slot —
// the payload for a later save (holes 1-6) is a strict superset of the
// earlier one (holes 1-5), so replacing rather than stacking keeps the
// queue tight without dropping any information. Deliberately does NOT
// include a per-hole discriminator; that produced N queue entries for a
// single player during a round and made drains slower than necessary.
function keyOf(entry) {
  return `${entry.action}|${entry.payload?.standalone_round_id || ''}|${entry.payload?.player_id || ''}`
}

export function enqueueWrite(action, payload) {
  const queue = readQueue()
  const stamped = {
    action,
    payload: { ...payload, client_updated_at: Date.now() },
    queued_at: Date.now(),
    attempts: 0,
  }
  const k = keyOf(stamped)
  const filtered = queue.filter(e => keyOf(e) !== k)
  filtered.push(stamped)
  writeQueue(filtered)
}

export function queueLength() {
  return readQueue().length
}

// Move an entry to the dead-letter store so a permanent failure doesn't
// silently vanish. Bounded ring-buffer semantics via writeQueue's slice.
function pushDeadLetter(entry, reason) {
  try {
    const raw = localStorage.getItem(DEAD_LETTER_KEY)
    const list = raw ? JSON.parse(raw) : []
    const arr = Array.isArray(list) ? list : []
    arr.push({ ...entry, dead_reason: reason, dead_at: Date.now() })
    localStorage.setItem(DEAD_LETTER_KEY, JSON.stringify(arr.slice(-MAX_DEAD_LETTER)))
  } catch { /* quota — best-effort */ }
}

// Attempt to drain the queue. `sender(action, payload)` should throw on
// network failure and resolve on success. Successful entries are dropped;
// per-entry attempt counter guards against a poison entry (permanently
// bad payload) freezing the drain forever — once an entry hits
// MAX_ATTEMPTS it moves to dead-letter and the drain proceeds. Network-
// like failures (fetch abort, offline) still stop the loop so we don't
// hammer a dead network with N sequential timeouts.
export async function flushPending(sender) {
  const queue = readQueue()
  if (queue.length === 0) return { drained: 0, remaining: 0, dropped: 0 }
  let drained = 0
  let dropped = 0
  const remaining = []
  for (let i = 0; i < queue.length; i++) {
    const entry = queue[i]
    try {
      await sender(entry.action, entry.payload)
      drained++
    } catch (err) {
      entry.attempts = (entry.attempts || 0) + 1
      // Distinguish "network dropped" (stop draining, keep everything)
      // from "server rejected" (drop after MAX_ATTEMPTS so the queue
      // moves on). A missing navigator.onLine is treated as online —
      // desktop browsers historically report false positives, so use
      // the check only to elevate confidence, not gate the classifier.
      const isNetworkish = err?.name === 'TypeError'
        || err?.name === 'AbortError'
        || (typeof navigator !== 'undefined' && navigator.onLine === false)
      if (isNetworkish) {
        remaining.push(entry, ...queue.slice(i + 1))
        break
      }
      if (entry.attempts >= MAX_ATTEMPTS) {
        pushDeadLetter(entry, err?.message || String(err))
        dropped++
        continue
      }
      // Non-network + still under attempts cap: keep at head, let the
      // next drain try again. Don't break — a bad payload shouldn't
      // block subsequent entries for a different player forever.
      remaining.push(entry)
    }
  }
  writeQueue(remaining)
  return { drained, remaining: remaining.length, dropped }
}

// Register drain-on-visible + drain-on-online listeners. Returns a
// cleanup function. The scorecard mount installs this; the listeners
// stay wired for the duration of the session.
export function installDrainListeners(sender) {
  const drain = () => { flushPending(sender).catch(() => {}) }
  const onVis = () => { if (document.visibilityState === 'visible') drain() }
  window.addEventListener('online', drain)
  document.addEventListener('visibilitychange', onVis)
  // Best-effort first drain in case something was queued from a prior
  // session before this handler mounted.
  drain()
  return () => {
    window.removeEventListener('online', drain)
    document.removeEventListener('visibilitychange', onVis)
  }
}
